import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { Organization } from '@/types/settings-api';
import { buildOrgUpdateBodies } from './org-update-bodies';

/**
 * `PUT /api/Organization/{organizationId}` — save org details and licence
 * fields. Flutter's `UpdateOrgDataEvent` (`settings_data_source_impl.dart:1204`).
 *
 * Body is built by `buildOrgUpdateBodies`, which strips `imageBase64` — a
 * client-only staging field for the logo picker that is absent from Flutter's
 * `toJson` and was previously uploaded into this body and discarded server-side
 * (PROD-1764).
 *
 * PROD-1766 — the second call Flutter makes and React did not:
 * `POST /api/Organization/UpdateContactDetails/{orgId}` for the contact and
 * billing payload, which Flutter strips out of the PUT above. Without it,
 * editing Contact Details did not persist at all.
 *
 * Sequential and fail-fast, as Flutter is: it awaits in order and fails the whole
 * operation on either call. Endpoint verbatim from
 * `settings_data_source_impl.dart:1245-1248` + `api_constants.dart:487` — NO
 * `/api/v1/` prefix, orgId as a PATH SEGMENT, both the recurring drift class here.
 *
 * ⚠️ NOT VERIFIED AGAINST A LIVE WRITE. See `org-update-bodies.ts`: the PUT body
 * is deliberately left un-stripped so this change is purely additive. If the PUT
 * ignores those fields, this POST is what makes them persist; if it does persist
 * them, nothing regresses. Do not reproduce Flutter's eight-key strip until
 * someone has run the write.
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (org: Organization) => {
      if (!org.organizationId) throw new Error('Organization.organizationId is required');
      const { orgBody, contactBody } = buildOrgUpdateBodies(org);
      await put<unknown>(`/api/Organization/${org.organizationId}`, orgBody);
      return post<unknown>(
        `/api/Organization/UpdateContactDetails/${org.organizationId}`,
        contactBody,
      );
    },
    onSuccess: (_data, org) => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'organization', org.organizationId],
      });
    },
  });
}
